// Copyright (c) Docugami, Inc. All rights reserved.

import { IIconProps } from "@fluentui/react";
import { UnassignedDocSetId } from "components/file_management/file_helpers";
import { themeOrange, themeRed, themeYellow } from "components/shell/presentation/AppStyles";
import { ClusterDocument, ClusterInfo, DocumentStatus, IngestionIssues, IngestionStatus } from "networking/api";
import { DocsetDocument } from "store/frontend/frontendSlice";
import { FileProperties } from "./FileManager";

export interface FileStatusInfo {
    status: FileStatus;
    category: FileStatusCategory;
    color: string;
    message: string;
    details: string;
    iconProps?: IIconProps;
}

export enum FileStatusCategory {
    Preparing = 'Preparing',
    Processing = 'Processing',
    NeedsAttention = 'NeedsAttention',
    Pending = 'Pending',
    Ready = 'Ready',
    Error = 'Error',
    OverQuota = 'OverQuota'
}

export enum FileStatus {
    // Preparing
    New = 'New',
    Ingesting = 'Ingesting',
    // Needs attention
    IngestedNoDocset = 'IngestedNoDocset',
    // Pending
    IngestedIncompleteDocset = 'IngestedIncompleteDocset',
    IngestedDocset = 'IngestedDocset',
    // Processing
    SdgmlProcessing = 'SdgmlProcessing',
    // Ready
    Ready = 'Ready',
    // Error types
    SdgmlError = 'SdgmlError',
    IngestionError = 'IngestionError',
    // Over quota
    OverQuota = 'OverQuota'
}

interface FileStatusInput {
    documentId: string;
    documentStatus: DocumentStatus;
    hasCpml: boolean;
    ingestionStatus?: IngestionStatus;
    ingestionIssues?: IngestionIssues;
    docSet: ClusterInfo | undefined;
    docSetDocThreshold: number | undefined;
}

export function getFileStatusInfo(file: FileProperties, docSet?: ClusterInfo, docSetDocThreshold?: number): FileStatusInfo {
    return getStatusInfo({
        documentId: file.folderId ?? '',
        documentStatus: file.status,
        hasCpml: !!file.hasCpml,
        ingestionStatus: file.ingestionStatus,
        ingestionIssues: file.ingestionIssues,
        docSet: docSet,
        docSetDocThreshold: docSetDocThreshold
    });
}

export function getDocumentStatusInfo(document: ClusterDocument | DocsetDocument, docSet?: ClusterInfo, docSetDocThreshold?: number): FileStatusInfo {
    return getStatusInfo({
        documentId: document.folderId,
        documentStatus: document.status,
        hasCpml: !!document.hasCpml,
        ingestionStatus: document.ingestionStatus,
        ingestionIssues: document.ingestionIssues,
        docSet: docSet,
        docSetDocThreshold: docSetDocThreshold
    });
}

function getStatusInfo(input: FileStatusInput): FileStatusInfo {
    const status = mapDocumentStatusToFileStatus(input);
    const statusCategory = mapFileStatusToCategory(status);

    const iconProps = getStatusIcon(statusCategory);
    return {
        status: status,
        category: statusCategory,
        color: iconProps?.color ?? '',
        message: getStatusCategoryLabel(statusCategory),
        details: getFileStatusDetails(status, input.ingestionIssues, input.docSet),
        iconProps: iconProps
    }
}

function mapDocumentStatusToFileStatus(input: FileStatusInput) {
    const { documentStatus, hasCpml, ingestionStatus, docSet, docSetDocThreshold } = input;
    let status = documentStatus;
    if (status === DocumentStatus.Ready) {
        // A document should only be ready if it is in a docset, and has cpml.
        // Otherwise it should be treated as Ingested.
        if (!isAssigned(docSet) || !hasCpml) {
            status = DocumentStatus.Ingested;
        }
    }

    switch (status) {
        case DocumentStatus.New:
            return FileStatus.New
        case DocumentStatus.Ingesting:
            return FileStatus.Ingesting;
        case DocumentStatus.Ingested:
            return getIngestedDocStatus(docSet, docSetDocThreshold);
        case DocumentStatus.Processing:
            return FileStatus.SdgmlProcessing;
        case DocumentStatus.Ready:
            return FileStatus.Ready;
        case DocumentStatus.OverQuota:
            return FileStatus.OverQuota;
        case DocumentStatus.Error:
            return ingestionStatus !== IngestionStatus.Succeeded ? FileStatus.IngestionError : FileStatus.SdgmlError;
        default:
            return FileStatus.New;
    }
}

// Helper to get status for successfully ingested documents, based on the docset state
function getIngestedDocStatus(docSet: ClusterInfo | undefined, docSetDocThreshold: number | undefined) {
    if (!isAssigned(docSet)) {
        return FileStatus.IngestedNoDocset;
    }
    else {
        if (docSetDocThreshold === undefined || docSet!.numberOfDocuments >= docSetDocThreshold) {
            return FileStatus.IngestedDocset;
        } else {
            return FileStatus.IngestedIncompleteDocset;
        }
    }
}


function mapFileStatusToCategory(status: FileStatus) {
    switch (status) {
        case FileStatus.New:
        case FileStatus.Ingesting:
            return FileStatusCategory.Preparing
        case FileStatus.IngestedNoDocset:
            return FileStatusCategory.NeedsAttention;
        case FileStatus.IngestedIncompleteDocset:
        case FileStatus.IngestedDocset:
            return FileStatusCategory.Pending;
        case FileStatus.SdgmlProcessing:
            return FileStatusCategory.Processing;
        case FileStatus.Ready:
            return FileStatusCategory.Ready;
        case FileStatus.SdgmlError:
        case FileStatus.IngestionError:
            return FileStatusCategory.Error;
        case FileStatus.OverQuota:
            return FileStatusCategory.OverQuota;
        default:
            throw new Error(`Unsupported file status: ${status}`);
    }
}

export function getFileStatusDetails(status: FileStatus, ingestionIssues?: IngestionIssues, docSet?: ClusterInfo) {
    const readyUnassigned = "Docugami has prepared this document. Assign it to a doc set to get started.";
    const readyAssigned = "Document is processed and ready to use.";
    const ingesting = "Document is being prepared.";
    const assignedProcessing = "Document is being processed for your doc set.";
    const overPageQuota = "Workspace page quota exceeded. Please upgrade your account to process this document.";
    const incompleteAssigned = `This document cannot be processed until the ${docSet?.label?.toLocaleUpperCase() + ' '}doc set is complete. Please add more docs to your doc set.`;
    const pending = "Document is being pending processing. If this is taking longer than expected, please contact a Docugami representative.";

    switch (status) {
        case FileStatus.New:
        case FileStatus.Ingesting:
            return ingesting;
        case FileStatus.IngestedNoDocset:
            return readyUnassigned;
        case FileStatus.IngestedIncompleteDocset:
            return incompleteAssigned;
        case FileStatus.IngestedDocset:
            return pending;
        case FileStatus.SdgmlProcessing:
            return assignedProcessing;
        case FileStatus.Ready:
            return readyAssigned;
        case FileStatus.OverQuota:
            return overPageQuota;
        case FileStatus.SdgmlError:
        case FileStatus.IngestionError:
            return getIngestionIssuesMessage(ingestionIssues);
        default:
            throw new Error(`Unsupported file status: ${status}`);
    }
}

function getIngestionIssuesMessage(ingestionIssues?: IngestionIssues): string {
    if (!ingestionIssues) {
        return "";
    }

    if (ingestionIssues & IngestionIssues.FileTypeNotSupported) {
        return "File type not supported. Currently, we only support DOCX and PDF.";
    }

    if (ingestionIssues & (IngestionIssues.EncryptedPdf | IngestionIssues.Encrypted)) {
        return "This file is encrypted. Please remove encryption and upload the new version.";
    }

    if (ingestionIssues & (IngestionIssues.FileSizeExceeded | IngestionIssues.PageCountExceeded)) {
        return "File size exceeded. Please contact your Docugami representative.";
    }

    if (ingestionIssues & (IngestionIssues.InvalidPdf | IngestionIssues.FileSizeZero)) {
        return "This file is invalid or corrupted and cannot be processed.";
    }

    return "File error. Please contact your Docugami representative.";
}

export function getStatusCategoryLabel(category: FileStatusCategory): string {
    switch (category) {
        case FileStatusCategory.Ready:
            return 'Ready';
        case FileStatusCategory.Preparing:
            return 'Preparing';
        case FileStatusCategory.Processing:
            return 'Processing';
        case FileStatusCategory.NeedsAttention:
            return 'Needs Attention';
        case FileStatusCategory.Pending:
            return 'Pending'
        case FileStatusCategory.Error:
            return 'Error'
        case FileStatusCategory.OverQuota:
            return 'Over Quota'
        default:
            return '';
    }
}

export function getStatusIcon(status: FileStatusCategory): IIconProps | undefined {
    switch (status) {
        case FileStatusCategory.Ready:
            return {
                iconName: 'StatusCircleOuter',
                color: '#82d96c'
            }
        case FileStatusCategory.Preparing:
            return {
                iconName: 'Contrast',
                color: themeYellow
            }
        case FileStatusCategory.Processing:
            return {
                iconName: 'Contrast',
                color: '#82d96c'
            }
        case FileStatusCategory.NeedsAttention:
            return {
                iconName: 'AlertSolid',
                color: themeOrange
            }
        case FileStatusCategory.Pending:
            return {
                iconName: 'Blocked12',
                color: 'purple'
            }
        case FileStatusCategory.Error:
            return {
                iconName: 'StatusCircleOuter',
                color: themeRed
            }
        case FileStatusCategory.OverQuota:
            return {
                iconName: 'IncidentTriangle',
                color: themeOrange
            }
        default:
            return;
    }
}

const isAssigned = (docset: ClusterInfo | undefined) => !!docset && docset.clusterId !== UnassignedDocSetId;
